import type { Citation, Message } from '../state/chatTypes';
import { isValidMessageRole } from '../state/messageRole';
import { parseVisualizationData } from '../structuredQuery/parse-visualization-data';
import type { VisualizationData } from '../structuredQuery/types';
import { parseWireActionResult, type StreamActionResult } from './actionResult';
import { parseWireArtifacts } from './wireArtifacts';

/**
 * Spec `Error` shape on stream frames and HTTP error envelopes.
 * `code` is one of {@link SIDEKICK_SPEC_ERROR_CODES}.
 */
export type StreamError = {
  code: string;
  message: string;
  retryable?: boolean;
  retryAfterSeconds?: number;
  details?: Record<string, string>;
};

/** Visualization placement on a stream `visualization` frame. */
export type StreamVisualizationPosition = 'inline' | 'sidebar' | 'modal';

const STREAM_VISUALIZATION_POSITIONS = ['inline', 'sidebar', 'modal'] as const;

const TOOL_ACTIVITY_STATUSES = ['started', 'completed', 'failed'] as const;

const STREAM_DONE_REASONS = ['completed', 'stopped'] as const;
/** Why a turn ended, from `StreamDone.reason`. */
export type StreamDoneReason = (typeof STREAM_DONE_REASONS)[number];

const isStreamDoneReason = (value: unknown): value is StreamDoneReason =>
  typeof value === 'string' &&
  (STREAM_DONE_REASONS as ReadonlyArray<string>).includes(value);
type ToolActivityStatus = (typeof TOOL_ACTIVITY_STATUSES)[number];

/**
 * Runtime-typed `StreamEvent` envelope from the Sidekick SSE channel.
 * Mirrors `conversations.proto` — `eventId` plus exactly one `oneof`
 * arm (`textDelta`, `messageDone`, `visualization`, `toolActivity`,
 * `done`, `error`, `actionResult`). The authorization
 * service writes `actionResult` frames itself, so their `eventId` is
 * always empty.
 * Host supplies the Sidekick API service base inside `apiBaseUrl`;
 * transport never prepends a version or service segment.
 */
export type StreamEvent =
  | { eventId: string; textDelta: { delta: string } }
  | { eventId: string; messageDone: { message: Message } }
  | {
      eventId: string;
      visualization: {
        visualization: VisualizationData;
        position: StreamVisualizationPosition;
      };
    }
  | {
      eventId: string;
      toolActivity: {
        toolName: string;
        status: ToolActivityStatus;
        description: string;
      };
    }
  | {
      eventId: string;
      done: {
        traceId: string;
        totalTokens?: number;
        latencyMs?: number;
        reason?: StreamDoneReason;
      };
    }
  | {
      eventId: string;
      error: { error: StreamError; shouldReconnect: boolean };
    }
  | {
      eventId: string;
      /** The turn's message id, as `SendMessageResponse` returned it. */
      messageId?: string;
      actionResult: StreamActionResult;
    };

const isToolActivityStatus = (value: unknown): value is ToolActivityStatus =>
  typeof value === 'string' &&
  (TOOL_ACTIVITY_STATUSES as ReadonlyArray<string>).includes(value);

const isStreamVisualizationPosition = (
  value: unknown
): value is StreamVisualizationPosition =>
  typeof value === 'string' &&
  (STREAM_VISUALIZATION_POSITIONS as ReadonlyArray<string>).includes(value);

const recordFromUnknown = (value: unknown): Record<string, unknown> | null => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
};

const stringFromRecord = (
  raw: Record<string, unknown>,
  key: string
): string => {
  const value = raw[key];
  return typeof value === 'string' ? value : '';
};

const numberFromRecord = (
  raw: Record<string, unknown>,
  key: string
): number | undefined => {
  const value = raw[key];
  return typeof value === 'number' ? value : undefined;
};

const parseStreamError = (raw: Record<string, unknown>): StreamError | null => {
  const code = stringFromRecord(raw, 'code');
  if (code === '') {
    return null;
  }
  const retryable = raw.retryable;
  const retryAfterSeconds = raw.retryAfterSeconds;
  const details = raw.details;
  return {
    code,
    message: stringFromRecord(raw, 'message'),
    ...(typeof retryable === 'boolean' ? { retryable } : {}),
    ...(typeof retryAfterSeconds === 'number' ? { retryAfterSeconds } : {}),
    ...(details !== null &&
    typeof details === 'object' &&
    !Array.isArray(details)
      ? { details: details as Record<string, string> }
      : {}),
  };
};

const parseWireCitations = (
  raw: unknown
): ReadonlyArray<Citation> | undefined => {
  if (!Array.isArray(raw) || raw.length === 0) {
    return undefined;
  }
  const citations = raw.reduce<Citation[]>((acc, entry) => {
    const record = recordFromUnknown(entry);
    if (record === null) {
      return acc;
    }
    const index = record.index;
    const displayLabel = stringFromRecord(record, 'displayLabel').trim();
    if (
      typeof index !== 'number' ||
      !Number.isInteger(index) ||
      index <= 0 ||
      displayLabel === ''
    ) {
      return acc;
    }
    const url = stringFromRecord(record, 'url');
    acc.push({ index, displayLabel, ...(url !== '' ? { url } : {}) });
    return acc;
  }, []);
  return citations.length > 0 ? citations : undefined;
};

const parseWireMessage = (raw: Record<string, unknown>): Message | null => {
  const id = stringFromRecord(raw, 'id');
  const conversationId = stringFromRecord(raw, 'conversationId');
  const roleRaw = stringFromRecord(raw, 'role');
  const content = stringFromRecord(raw, 'content');
  const createdAt = stringFromRecord(raw, 'createdAt');
  if (id === '' || conversationId === '' || createdAt === '') {
    return null;
  }
  const role = isValidMessageRole(roleRaw) ? roleRaw : 'assistant';
  const name = stringFromRecord(raw, 'name');
  const metadataRaw = raw.metadata;
  const metadata =
    metadataRaw !== null &&
    typeof metadataRaw === 'object' &&
    !Array.isArray(metadataRaw) &&
    Object.keys(metadataRaw).length > 0
      ? (metadataRaw as Record<string, string>)
      : undefined;
  const vizUrlsRaw = raw.visualizationUrls;
  const visualizationUrls =
    Array.isArray(vizUrlsRaw) && vizUrlsRaw.length > 0
      ? vizUrlsRaw
          .filter((u): u is string => typeof u === 'string' && u !== '')
          .map((url, index) => ({ url, position: index }))
      : undefined;
  const citations = parseWireCitations(raw.citations);
  const artifacts = parseWireArtifacts(raw.artifacts);
  const parentId = stringFromRecord(raw, 'parentId');

  return {
    id,
    conversationId,
    role,
    content,
    createdAt,
    ...(name !== '' ? { name } : {}),
    ...(metadata !== undefined ? { metadata } : {}),
    ...(visualizationUrls !== undefined ? { visualizationUrls } : {}),
    ...(citations !== undefined ? { citations } : {}),
    ...(artifacts !== undefined ? { artifacts } : {}),
    ...(parentId !== '' ? { parentId } : {}),
  };
};

/**
 * Parses the raw `data:` JSON of one SSE record into a spec-shaped
 * {@link StreamEvent}, or `null` when the payload is not valid JSON,
 * lacks `eventId` (an `actionResult` frame never has one), or has no
 * populated `oneof` arm. Callers MUST treat `null` as non-fatal and
 * continue draining the stream.
 */
export const parseStreamEvent = (rawData: string): StreamEvent | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawData);
  } catch {
    return null;
  }

  const root = recordFromUnknown(parsed);
  if (root === null) {
    return null;
  }

  const eventId = stringFromRecord(root, 'eventId');

  const actionResult = parseWireActionResult(root.actionResult);
  if (actionResult !== null) {
    const messageId = stringFromRecord(root, 'messageId');
    return {
      eventId,
      ...(messageId !== '' ? { messageId } : {}),
      actionResult,
    };
  }

  if (eventId === '') {
    return null;
  }

  const textDelta = recordFromUnknown(root.textDelta);
  if (textDelta !== null) {
    const delta = stringFromRecord(textDelta, 'delta');
    if (delta !== '') {
      return { eventId, textDelta: { delta } };
    }
  }

  const toolActivity = recordFromUnknown(root.toolActivity);
  if (toolActivity !== null) {
    const toolName = stringFromRecord(toolActivity, 'toolName');
    if (toolName !== '') {
      const rawStatus = toolActivity.status;
      const status = isToolActivityStatus(rawStatus) ? rawStatus : 'started';
      return {
        eventId,
        toolActivity: {
          toolName,
          status,
          description: stringFromRecord(toolActivity, 'description'),
        },
      };
    }
  }

  const visualizationArm = recordFromUnknown(root.visualization);
  if (visualizationArm !== null) {
    const vizRaw = recordFromUnknown(visualizationArm.visualization);
    const positionRaw = visualizationArm.position;
    if (vizRaw !== null && isStreamVisualizationPosition(positionRaw)) {
      const visualization = parseVisualizationData(vizRaw);
      if (visualization !== null) {
        return {
          eventId,
          visualization: { visualization, position: positionRaw },
        };
      }
    }
  }

  const messageDone = recordFromUnknown(root.messageDone);
  if (messageDone !== null) {
    const messageRaw = recordFromUnknown(messageDone.message);
    if (messageRaw !== null) {
      const message = parseWireMessage(messageRaw);
      if (message !== null) {
        return { eventId, messageDone: { message } };
      }
    }
  }

  // Every frame carries every arm with empty defaults, so a done counts
  // only when it names a trace or a reason. A stopped turn may have no trace.
  const done = recordFromUnknown(root.done);
  if (done !== null) {
    const traceId = stringFromRecord(done, 'traceId');
    const reason = isStreamDoneReason(done.reason) ? done.reason : undefined;
    if (traceId !== '' || reason !== undefined) {
      return {
        eventId,
        done: {
          traceId,
          ...(reason !== undefined ? { reason } : {}),
          ...(numberFromRecord(done, 'totalTokens') !== undefined
            ? { totalTokens: numberFromRecord(done, 'totalTokens') }
            : {}),
          ...(numberFromRecord(done, 'latencyMs') !== undefined
            ? { latencyMs: numberFromRecord(done, 'latencyMs') }
            : {}),
        },
      };
    }
  }

  const errorArm = recordFromUnknown(root.error);
  if (errorArm !== null) {
    const nestedError = recordFromUnknown(errorArm.error);
    if (nestedError !== null) {
      const streamError = parseStreamError(nestedError);
      if (streamError !== null) {
        return {
          eventId,
          error: {
            error: streamError,
            shouldReconnect:
              typeof errorArm.shouldReconnect === 'boolean'
                ? errorArm.shouldReconnect
                : false,
          },
        };
      }
    }
  }

  return null;
};
